"use client";
import { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocale } from "next-intl";
import Link from "next/link";
import { useVisibleNavLinks } from "@/hooks/useVisibleNavLinks";
import LanguageSwitcher from "./LanguageSwitcher";
import Image from "next/image";
import MobileMenu from "./MobileMenu";
import { usePathname } from "next/navigation";
// import { usePathname } from "next/navigation";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const locale = useLocale();
  const isArabic = locale === "ar";
  const navRef = useRef<HTMLDivElement>(null);
  const navLinks = useVisibleNavLinks();

  const pathname = usePathname()?.replace(/^\/(ar|en)/, "") || "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      if (window.scrollY > 50) setOpenDropdown(null);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleDropdown = (label: any) => {
    setOpenDropdown((prev) => (prev === label ? null : label));
  };

  return (
    <motion.nav
      ref={navRef}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`transition-all duration-500 bg-white py-2 z-100 w-full sticky top-0 shadow-md`}
    >
      <div className="container mx-auto px-6  flex items-center justify-between">
        <Link
          href="/"
          className="relative z-10 transition-transform active:scale-95"
        >
          <div className=" p-2 rounded-sm">
            <Image
              src="/logo.png"
              alt="Dawlia 21"
              width={65}
              height={80}
              className="object-contain lg:w-[70px]"
              priority
            />
          </div>
        </Link>

        <ul className="hidden lg:flex items-center gap-10">
          {navLinks.map((link: any) => (
            <li
              key={link.to}
              className="relative group"
              onMouseEnter={() =>
                link.children && setOpenDropdown(link.label_en)
              }
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <Link
                href={link.to}
                className={`flex items-center gap-1.5  font-bold text-[11px] uppercase tracking-[0.2em] transition-colors duration-300
                  text-black hover:text-brand-blue `}
              >
                {isArabic ? link.label_ar : link.label_en}
                {link.children && (
                  <ChevronDown
                    size={14}
                    className="group-hover:rotate-180 transition-transform duration-300"
                  />
                )}
              </Link>

              <span
                className={`absolute -bottom-1 left-0 h-0.5 bg-brand-blue transition-all duration-300 w-0 group-hover:w-full ${link.to === pathname && "w-1/4"}`}
              />

              {/* Dropdown Menu */}
              <AnimatePresence>
                {link.children && openDropdown === link.label_en && (
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 15 }}
                    className="absolute top-full left-0 mt-4 w-60 bg-brand-creamy shadow-2xl border-t-4 border-brand-blue p-2 overflow-hidden"
                  >
                    {link.children.map((child: any) => (
                      <Link
                        key={child.to}
                        href={child.to}
                        className="block px-4 py-3 text-xs font-black text-brand-navy hover:bg-brand-navy hover:text-brand-creamy transition-all duration-200"
                      >
                        {isArabic ? child.label_ar : child.label_en}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-6">
          <div
            className={`hidden sm:block transition-colors duration-300 ${
              isScrolled ? "text-white" : "text-brand-navy"
            }`}
          >
            <LanguageSwitcher />
          </div>

          <Link href="/contact" className="hidden md:block">
            <button className="relative overflow-hidden group bg-brand-red/90 text-white px-7 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all duration-300 hover:shadow-[0_10px_20px_rgba(173,31,35,0.3)] active:scale-95">
              <span className="relative z-10 flex items-center gap-2">
                <Phone size={14} />
                {isArabic ? "تواصل معنا" : "Contact Us"}
              </span>
              <div className="absolute inset-0 bg-brand-red -translate-x-full group-hover:translate-x-0 transition-transform duration-500 shadow-inner"></div>
            </button>
          </Link>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-3 transition-all duration-300 shadow-lg bg-brand-red text-white`}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <MobileMenu
        isMobileMenuOpen={isMobileMenuOpen}
        toggleDropdown={toggleDropdown}
        openDropdown={openDropdown}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
      />
    </motion.nav>
  );
}
